import React, { useState } from 'react'
import classNames from 'classnames'
import { PaymentPlanPoint } from './PaymentPlanPoint'
import { AdvertiserRadio } from '../../../commonComponents/hire/AdvertiserRadio'
import { AdvertiserButton } from '../../../commonComponents/hire/AdvertiserButton'
import DoneRoundedIcon from '@mui/icons-material/DoneRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

// Small helper subcomponent for the 3 mobile columns
const PlanMetaColumn = ({ label, value }) => {
  // If `value` is truthy, show a green tick with value; otherwise show a red cross
  const isAvailable = !!value

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-1">
      <div className="text-sm leading-18 text-[#595959]">{label}</div>
      <div className="flex items-center gap-[2px]">
        {isAvailable ? (
          <DoneRoundedIcon className="h-5 w-5 text-[#028A12]" />
        ) : (
          <CloseRoundedIcon className="h-5 w-5 text-[#B3011D]" />
        )}
        {isAvailable && (
          <span className="text-base leading-20 text-[#323232]">{isAvailable ? value : ''}</span>
        )}
      </div>
    </div>
  )
}

export const PaymentPlanCard = ({
  id = null,
  title = '',
  amount = 0,
  crossed_amount = 0,
  tags = [],
  highlights = [],

  // Additional fields from your backend
  validity_duration = null,
  verified_leads = null,
  database_leads = null,

  isSelected = false,
  showRadio = true,
  onSelect = null,
  onActionClick = null,
  actionButtonLabel = 'Buy',
  containerClassName = 'flex-1',
  tagColor = 'text-[#F4F5F9] bg-[#F4F5F9]',
  windowSize = 840
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)

  // Check if user is on mobile
  const isMobile = windowSize <= 768
  // Check if crossed_amount is valid
  const hasCrossedAmount = crossed_amount && crossed_amount > amount

  // Calculate discount
  const discountPercentage = hasCrossedAmount
    ? Math.round(100 - (amount / crossed_amount) * 100)
    : 0

  // Check for "Most Popular" tag
  const isMostPopular = tags.some((tag) => tag.toLowerCase().replace(/\s/g, '') === 'mostpopular')

  const handleCardClick = () => {
    // If the card is selectable (radio style), notify parent
    if (showRadio && onSelect) {
      onSelect(id)
    }
  }

  const handleActionClick = (e) => {
    // Prevent card click from triggering if there's a button
    e.stopPropagation()
    if (onActionClick) {
      onActionClick(id)
    }
  }

  const borderClass = isSelected
    ? 'border-[3px] border-[#0174d3]'
    : isMostPopular && !showRadio
    ? 'border-[2px] border-[#0174d3]' 
    : 'border-xs border-[#cbcbcb]'

  return (
    <div className={containerClassName}>
      {isMostPopular ? (
        <div
          className={classNames(
            'relative z-50 flex items-center justify-center rounded-tl-sm rounded-tr-sm bg-[#0174d3] pt-[5px] font-bold text-[white]',
            isMobile ? 'mb-[-6px] w-[120px] pb-[10px] text-[11px]' : 'mb-[-6px] pb-[8px] text-sm'
          )}
        >
          MOST POPULAR
        </div>
      ) : (
        <div
          className={classNames(
            `relative z-50 flex items-center justify-center rounded-tl-sm rounded-tr-sm pt-[5px] font-bold ${tagColor}`,
            isMobile ? 'mb-[-6px] w-[120px] pb-[8px] text-[11px]' : 'mb-[-6px] pb-[8px] text-sm'
          )}
        >
          MOST POPULAR
        </div>
      )}
      <div
        onClick={handleCardClick}
        className={classNames(
          'relative z-50 flex cursor-pointer flex-col gap-4 rounded-sm border-solid bg-white px-16',
          windowSize > 768 ? 'py-24' : 'pt-24 pb-[0px]',
          borderClass
        )}
      >
        {/* Header (Title + Price + Radio) */}
        <div className="flex items-start gap-2">
          {/* Left side: Title + Price Details */}
          <div id="plan_info" className="flex flex-1 flex-col gap-2">
            <div id="plan_title" className="text-[18px] font-bold leading-27 text-[#191919]">
              {title}
            </div>
            <div id="plan_price_details" className="flex items-center gap-1">
              <div id="amount" className="text-[24px] font-bold leading-[30px] text-[#323232]">
                ₹{amount}
              </div>
              {hasCrossedAmount && (
                <div id="crossed_amount" className="text-base leading-21 text-[#666] line-through">
                  ₹{crossed_amount}
                </div>
              )}
              {hasCrossedAmount && (
                <div
                  id="discount"
                  className="inline-flex items-center justify-center rounded-[24px] border-xs border-solid border-[#0174D3] px-xs pt-[2px] pb-[4px] text-[11px] font-bold leading-[14px] text-[#0174d3]"
                >
                  {discountPercentage}% off
                </div>
              )}
            </div>
          </div>

          {/* Right side: Radio Button */}
          {showRadio && (
            <div id="radio_container" className="flex items-center">
              <AdvertiserRadio checked={isSelected} onChange={() => onSelect && onSelect(id)} />
            </div>
          )}
        </div>

        {/* Action button (if NOT showing radio) */}
        {!showRadio && onActionClick && (
          <AdvertiserButton
            onClick={handleActionClick}
            label={actionButtonLabel}
            className="h-10 w-full border-xs border-solid border-[#cbcbcb] bg-[#fff] text-base font-bold leading-24 text-title outline-none"
          />
        )}

        {/* Divider */}
        <div className="h-[1px] bg-[#e5e5e5]" />

        {/* Below the divider: differ for mobile vs. desktop */}
        {isMobile ? (
          <>
            {/* 3-column row: Validity / Verified / Database */}
            <div className="flex items-center gap-4">
              <PlanMetaColumn label="Validity" value={validity_duration} />
              <div className="h-[42px] w-[1px] bg-[#e5e5e5]"></div>
              <PlanMetaColumn label="Verified" value={verified_leads} />
              <div className="h-[42px] w-[1px] bg-[#e5e5e5]"></div>
              <PlanMetaColumn label="Database" value={database_leads} />
            </div>

            {/** Collapsible container with transition */}

            <div
              className={classNames(
                'flex flex-col gap-3 overflow-hidden transition-[max-height] duration-300 ease-in-out',
                isDetailsOpen ? 'max-h-96' : 'max-h-0'
              )}
            >
              {highlights.map((highlight) => (
                <PaymentPlanPoint
                  key={highlight?.id}
                  description={highlight?.description}
                  isAvailable={highlight?.is_available}
                />
              ))}
            </div>

            {/* "View details" row for the highlights */}
            <div
              className="mx-[-16px] flex h-[35px] cursor-pointer justify-center gap-2 self-stretch rounded-br-sm rounded-bl-sm p-xs"
              style={{ background: 'linear-gradient(90deg, #E7F3FE 0%, #F9FCFE 100%)' }}
              onClick={(e) => {
                e.stopPropagation()
                setIsDetailsOpen(!isDetailsOpen)
              }}
            >
              <div className="text-sm leading-18 text-[#323232]">
                {isDetailsOpen ? 'Hide details' : 'View details'}
              </div>

              <div>
                {isDetailsOpen ? (
                  <ExpandLessIcon className="h-4 w-4 text-[#323232]" />
                ) : (
                  <ExpandMoreIcon className="h-4 w-4 text-[#323232]" />
                )}
              </div>
            </div>
          </>
        ) : (
          // Desktop: directly show the highlights list
          <div className="flex flex-col gap-3">
            {highlights.map((highlight) => (
              <PaymentPlanPoint
                key={highlight?.id}
                description={highlight?.description}
                isAvailable={highlight?.is_available}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
